import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { CartLine } from "../shared/types";

type CartContextValue = {
  lines: CartLine[];
  count: number;
  addLine: (line: Omit<CartLine, "id">) => string;
  updateLine: (id: string, line: Omit<CartLine, "id">) => void;
  setQuantity: (id: string, quantity: number) => void;
  removeLine: (id: string) => void;
  clear: () => void;
};

const STORAGE_KEY = "pressed-studio-cart-v1";
const CartContext = createContext<CartContextValue | null>(null);

function readCart(): CartLine[] {
  try {
    const value = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    return Array.isArray(value) ? value : [];
  } catch {
    return [];
  }
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [lines, setLines] = useState<CartLine[]>(() => readCart());

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(lines));
  }, [lines]);

  const value = useMemo<CartContextValue>(() => ({
    lines,
    count: lines.reduce((sum, line) => sum + line.quantity, 0),
    addLine(line) {
      const id = crypto.randomUUID();
      setLines((current) => [...current, { ...line, id }]);
      return id;
    },
    updateLine(id, line) {
      setLines((current) => current.map((candidate) => candidate.id === id ? { ...line, id } : candidate));
    },
    setQuantity(id, quantity) {
      setLines((current) => current.map((line) => line.id === id ? { ...line, quantity: Math.max(1, Math.min(10, quantity)) } : line));
    },
    removeLine(id) {
      setLines((current) => current.filter((line) => line.id !== id));
    },
    clear() {
      setLines([]);
    }
  }), [lines]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartContextValue {
  const value = useContext(CartContext);
  if (!value) throw new Error("useCart must be used inside CartProvider");
  return value;
}
